'use client'
import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {
  Menu,
  X,
  ChevronDown,
  // Industries
  Shirt,
  Sparkles,
  Gem,
  Home as HomeIcon,
  Sofa,
  Hotel,
  Tv,
  Dumbbell,
  Baby,
  PawPrint,
  HeartPulse,
  UtensilsCrossed,
  Car,
  Plane,
  // Widgets
  Instagram,
  Music2,
  Youtube,
  Twitter,
  Linkedin,
  AtSign,
  // Solutions
  Layers,
  Star,
  Building2,
  ShieldCheck,
  Trophy,
  // Resources
  LifeBuoy,
  Wrench,
  BookOpen,
  Code2,
  Images,
  Zap,
  Activity,
  Newspaper,
  FileText,
  ArrowUpRight,
  // Newly-built feature pages
  MessageSquare,
  Bell,
  Beaker,
  Tag,
  BarChart3,
  ScanSearch,
  Mail,
  Camera,
  Smartphone,
  FileSignature,
  Wand2,
  Upload,
  Heart,
  Target,
  Megaphone,
  Route,
  Link2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { brand } from '@/lib/copy/brand'
import { useTranslation } from '@/lib/i18n/use-translation'
import { rotatedCoverFor } from '@/lib/copy/wp-fallback'
import { SITE_COUNTS } from '@/lib/copy/site-counts.generated'

type NavLink = {
  label: string
  href: string
  description?: string
  icon?: React.ComponentType<{ className?: string }>
  badge?: string
  external?: boolean
}
type NavFeatured = {
  eyebrow: string
  title: string
  description: string
  image?: string
  ctaLabel: string
  ctaHref: string
  external?: boolean
}
type NavStat = { label: string; value: string }
type NavGroup =
  | { kind: 'link'; label: string; href: string; badge?: string }
  | {
      kind: 'mega'
      label: string
      // 1-line strapline shown under the panel heading
      tagline?: string
      // Quick-glance stats rendered as a strip above the columns
      stats?: NavStat[]
      // The keyword used to pick relevant imported blog posts for the
      // bottom strip ("From the idukki.io archive"). Matched against
      // page slug + h1.
      archiveQuery?: string | string[]
      columns: { heading: string; items: NavLink[] }[]
      featured?: NavFeatured
    }

// Pull a representative screenshot for menu featured cards. Fall back
// to the brand hero when no screenshot has been crawled yet.
const screenshots = brand.screenshots || []
const heroAsset = brand.assets?.hero
const pickShot = (i: number) =>
  screenshots[i % Math.max(screenshots.length, 1)]?.ref || heroAsset?.ref || ''

// Resolve N imported blog posts whose slug or H1 matches `query`. Used
// to populate the "From the idukki.io archive" strip at the bottom of
// each mega-menu panel with real, clickable content.
function resolveArchivePosts(query: string | string[] | undefined, n = 3) {
  if (!query) return []
  const terms = Array.isArray(query) ? query : [query]
  const lower = terms.map((t) => t.toLowerCase())
  const candidates = (brand.pages || []).filter((p) => {
    if (!p.slug || !p.body) return false
    if (!p.slug.startsWith('blog/')) return false
    const haystack = `${p.slug} ${p.h1 || p.title}`.toLowerCase()
    return lower.some((t) => haystack.includes(t))
  })
  return candidates.slice(0, n)
}

const nav: NavGroup[] = [
  {
    kind: 'mega',
    label: 'Product',
    tagline: 'Collect, clear rights, tag, display, convert and measure UGC in one 37 KB runtime.',
    stats: [
      { label: 'Layouts', value: '24+' },
      { label: 'Widget weight', value: '37 KB' },
      { label: 'Platforms', value: String(SITE_COUNTS.platforms) },
    ],
    archiveQuery: ['ugc', 'shoppable', 'widget', 'review'],
    columns: [
      {
        heading: 'Collect',
        items: [
          { icon: Upload, label: 'Collect UGC', href: '/collect-ugc', description: 'Hashtags, mentions, uploads, QR, email' },
          { icon: Activity, label: 'Social listening', href: '/social-listening', description: 'Mentions across eight networks' },
          { icon: Star, label: 'Reviews + ratings', href: '/reviews', description: 'Photo and video reviews with stars' },
          { icon: Camera, label: 'Video testimonials', href: '/video-testimonials', description: 'Record-in-browser, rights built in' },
          { icon: Trophy, label: 'UGC competitions', href: '/ugc-competitions', description: 'Voting, leaderboards, prizes' },
          { icon: MessageSquare, label: 'Surveys', href: '/surveys', description: 'Post-purchase questions that feed UGC' },
        ],
      },
      {
        heading: 'Rights + curate',
        items: [
          { icon: FileSignature, label: 'Rights management', href: '/rights-management', description: 'Consent, licences, revocation at scale' },
          { icon: ShieldCheck, label: 'Moderation', href: '/moderation', description: 'AI-assisted queue with rules' },
          { icon: Tag, label: 'AI tagging', href: '/ai-tagging', description: 'Vision AI maps posts to products' },
          { icon: ScanSearch, label: 'UGC Intelligence', href: '/ugc-intelligence', description: 'Find the best content, fast' },
          { icon: ShieldCheck, label: 'Brand safety', href: '/brand-safety', description: 'Keep unsafe content out' },
        ],
      },
      {
        heading: 'Display + convert',
        items: [
          { icon: Layers, label: 'Widgets + layouts', href: '/widgets', description: 'Galleries, carousels, walls, badges' },
          { icon: Zap, label: 'Shoppable video', href: '/shoppable-video', description: 'Reels, stories, shop the look' },
          { icon: Bell, label: 'Smart overlays', href: '/overlays', description: 'Social-proof popups tied to UGC' },
          { icon: Link2, label: 'Link in bio', href: '/link-in-bio', description: 'Shoppable bio page from your feed' },
          { icon: Mail, label: 'Lead capture', href: '/lead-capture', description: 'Email and SMS from the gallery' },
          { icon: Images, label: 'Amazon syndication', href: '/amazon', description: 'A+ Content, Posts and PDP video packs' },
        ],
      },
      {
        heading: 'Measure + optimise',
        items: [
          { icon: BarChart3, label: 'Analytics', href: '/analytics', description: 'Impressions to orders, per widget' },
          { icon: Target, label: 'Attribution', href: '/attribution', description: 'First, last and linear credit' },
          { icon: Beaker, label: 'A/B testing', href: '/ab-testing', description: 'Layout experiments with significance' },
          { icon: FileText, label: 'Orders ledger', href: '/orders', description: 'Every UGC-influenced order' },
          { icon: Sparkles, label: 'All features', href: '/features', description: 'The full list, grouped by job' },
        ],
      },
    ],
    featured: {
      eyebrow: 'Try it now',
      title: 'Build a real widget in your browser',
      description:
        'Pick a source and a layout, watch the live preview, copy the embed. No signup to try.',
      image: pickShot(2),
      ctaLabel: 'Open the widget builder',
      ctaHref: '/tools/widget-builder',
    },
  },
  {
    kind: 'mega',
    label: 'Industries',
    tagline: 'Pick your category or channel, see the layouts, benchmarks and case studies built for it.',
    stats: [
      { label: 'Verticals', value: '14' },
      { label: 'Channels', value: '8' },
      { label: 'Case studies', value: String(SITE_COUNTS.caseStudies) },
    ],
    archiveQuery: ['ugc', 'shopify', 'instagram', 'tiktok'],
    columns: [
      {
        heading: 'Retail + commerce',
        items: [
          {
            icon: Shirt,
            label: 'Fashion + apparel',
            href: '/industries/fashion-and-apparels',
            description: 'Shoppable lookbooks + on-model UGC',
            badge: 'POPULAR',
          },
          {
            icon: Sparkles,
            label: 'Beauty + skincare',
            href: '/industries/beauty-and-cosmetics',
            description: 'Routines, swatches, before/after',
          },
          {
            icon: Gem,
            label: 'Accessories + jewellery',
            href: '/industries/accessories-and-jewelery',
            description: 'Macro + lifestyle galleries',
          },
          {
            icon: HomeIcon,
            label: 'Home + lifestyle',
            href: '/industries/home-lifestyle',
            description: 'In-room visualisation + reviews',
          },
          {
            icon: Sofa,
            label: 'Furniture',
            href: '/industries/furniture',
            description: 'High-AOV social proof at PDP',
          },
          {
            icon: Baby,
            label: 'Baby + kids',
            href: '/industries/baby-and-kids',
            description: 'Parent reviews, safety stories, growth journeys',
          },
          {
            icon: PawPrint,
            label: 'Pet care',
            href: '/industries/pet-care',
            description: 'Pet parents, before/after, nutrition tracking',
          },
          {
            icon: HeartPulse,
            label: 'Health + wellness',
            href: '/industries/health-and-wellness',
            description: "Supplements, sleep, women's wellness, recovery",
            badge: 'NEW',
          },
          {
            icon: UtensilsCrossed,
            label: 'Food + beverage',
            href: '/industries/food-and-beverage',
            description: 'Recipes, tasting notes, unboxing, gifting',
            badge: 'NEW',
          },
        ],
      },
      {
        heading: 'Verticals',
        items: [
          {
            icon: Hotel,
            label: 'Hospitality',
            href: '/industries/hospitality',
            description: 'Property feeds + guest stories',
          },
          {
            icon: Plane,
            label: 'Travel + experiences',
            href: '/industries/travel-and-experiences',
            description: 'Trip reels, itineraries, packing lists',
            badge: 'NEW',
          },
          {
            icon: Tv,
            label: 'Consumer electronics',
            href: '/industries/consumer-electronics',
            description: 'Unboxing + tutorials',
          },
          {
            icon: Dumbbell,
            label: 'Sporting goods',
            href: '/industries/sporting-goods',
            description: 'Athlete + community UGC',
          },
          {
            icon: Car,
            label: 'Automobiles',
            href: '/industries/automobiles',
            description: 'Ownership reels, road-trips, EV charging',
            badge: 'NEW',
          },
        ],
      },
    
      {
        heading: 'By channel',
        items: [
          {
            icon: Layers,
            label: 'Layouts by industry',
            href: '/industries/galleries',
            description: 'Every placement, per vertical',
          },
          {
            icon: Instagram,
            label: 'Instagram',
            href: '/widgets/instagram',
            description: 'Feeds, reels, hashtag galleries',
            badge: 'POPULAR',
          },
          {
            icon: Music2,
            label: 'TikTok',
            href: '/widgets/tiktok',
            description: 'Shoppable shorts + creator tags',
          },
          {
            icon: Youtube,
            label: 'YouTube',
            href: '/widgets/youtube',
            description: 'Channels, playlists, shorts',
          },
          {
            icon: Twitter,
            label: 'X / Twitter',
            href: '/widgets/twitter',
            description: 'Live mentions + curated quotes',
          },
        ],
      },
      {
        heading: 'More sources',
        items: [
          {
            icon: Linkedin,
            label: 'LinkedIn',
            href: '/widgets/linkedin',
            description: 'B2B advocacy + thought leaders',
          },
          {
            icon: AtSign,
            label: 'Threads',
            href: '/widgets/threads',
            description: 'Fresh-channel UGC',
            badge: 'NEW',
          },
          {
            icon: Images,
            label: 'Pinterest',
            href: '/widgets/pinterest',
            description: 'Pin galleries + boards',
          },
          {
            icon: Layers,
            label: 'Facebook',
            href: '/widgets/facebook',
            description: 'Pages, posts, reviews',
          },
        ],
      },
    ],
    featured: {
      eyebrow: 'Widget galleries',
      title: 'See the layouts built for your category',
      description:
        'Industry-tuned layout galleries for every vertical: homepage reels, shoppable PDP video, community walls and review rails.',
      image: pickShot(2),
      ctaLabel: 'Open the widget galleries',
      ctaHref: '/industries/galleries',
    },
  },
  {
    kind: 'mega',
    label: 'Solutions',
    tagline: 'By role, by AI surface, and everything procurement will ask for.',
    stats: [
      { label: 'Case studies', value: String(SITE_COUNTS.caseStudies) },
      { label: 'Compare pages', value: String(SITE_COUNTS.competitors) },
      { label: 'Customer logos', value: `${(brand.customerLogos || []).length}+` },
    ],
    archiveQuery: ['ugc', 'review', 'shopify', 'rights'],
    columns: [
      {
        heading: 'By role',
        items: [
          { icon: Megaphone, label: 'CMO', href: '/solutions/cmo', description: 'Brand-safe UGC with proof of revenue' },
          { icon: Target, label: 'CRO', href: '/solutions/cro', description: 'Lift PDP conversion, keep CWV' },
          { icon: Code2, label: 'CTO', href: '/solutions/cto', description: 'One runtime, no theme surgery' },
          { icon: Building2, label: 'Ecommerce leaders', href: '/solutions/ecommerce-leader', description: 'Merchandise with customer content' },
          { icon: Route, label: 'Agencies', href: '/solutions/agency', description: 'Multi-client workspace and reporting' },
        ],
      },
      {
        heading: 'AI surfaces',
        items: [
          { icon: Smartphone, label: 'Virtual try-on', href: '/virtual-try-on', description: 'Shoppers try products on themselves' },
          { icon: ScanSearch, label: 'Visual search', href: '/visual-search', description: 'Photo in, matching products out' },
          { icon: MessageSquare, label: 'AI Shopper', href: '/ai-shopper', description: 'Answers from reviews and UGC' },
          { icon: Wand2, label: 'Smart retargeting', href: '/smart-retargeting', description: 'Bring back browsers with their own UGC' },
          { icon: Heart, label: 'Personalized feed', href: '/personalized-feed', description: 'Galleries that adapt per shopper' },
          { icon: Sparkles, label: 'Agentic commerce', href: '/agentic-commerce', description: 'Feeds AI agents can shop from' },
        ],
      },
      {
        heading: 'Proof',
        items: [
          { icon: FileText, label: 'Case studies', href: '/case-studies', description: 'Attributed revenue, per brand' },
          { icon: Images, label: 'Showcase', href: '/showcase', description: 'Live widgets on real stores' },
          { icon: Star, label: 'Customers', href: '/customers', description: 'Who runs Idukki today' },
          { icon: Activity, label: 'Widget speed proof', href: '/widget-health', description: 'Real Lighthouse runs, dated' },
        ],
      },
      {
        heading: 'For procurement',
        items: [
          { icon: ShieldCheck, label: 'Trust center', href: '/trust', description: 'Security, sub-processors, DPA' },
          { icon: Building2, label: 'Enterprise', href: '/enterprise', description: 'SSO, SLAs, white-label' },
          { icon: ShieldCheck, label: 'Security', href: '/security', description: 'How your data is handled' },
          { icon: Layers, label: 'Compare alternatives', href: '/compare', description: 'Side-by-side battlecards' },
        ],
      },
    ],
    featured: {
      eyebrow: 'Why Idukki',
      title: 'Replace six tools with one 37 KB embed.',
      description:
        'Side-by-side comparisons against every UGC, reviews and shoppable-video vendor we get asked about: pricing model, widget weight, attribution.',
      image: pickShot(11),
      ctaLabel: 'Compare alternatives',
      ctaHref: '/compare',
    },
  },
  { kind: 'link', label: 'Pricing', href: '/pricing' },
  {
    kind: 'mega',
    label: 'Resources',
    tagline: 'Guides, tools and help written to get the first widget live, then measured.',
    stats: [
      { label: 'Articles', value: String(SITE_COUNTS.articles) },
      { label: 'Free tools', value: String(SITE_COUNTS.freeTools) },
      { label: 'Help articles', value: String(SITE_COUNTS.helpArticles) },
    ],
    archiveQuery: ['ugc', 'review', 'instagram', 'shopify'],
    columns: [
      {
        heading: 'Learn',
        items: [
          { icon: LifeBuoy, label: 'Help center', href: '/help', description: 'Setup, integrations, troubleshooting' },
          { icon: BookOpen, label: 'Guides + ebooks', href: '/resources', description: `${SITE_COUNTS.guides} playbooks, briefs, benchmarks` },
          { icon: FileText, label: 'Industry reports', href: '/reports', description: 'Benchmarks and white papers' },
          { icon: Newspaper, label: 'Blog', href: '/blog', description: 'Teardowns, benchmarks, migrations' },
          { icon: Images, label: 'UGC examples', href: '/tools/social-proof-examples', description: '30+ implementations dissected' },
          { icon: Zap, label: 'What\u2019s new', href: '/whats-new', description: 'Monthly product updates' },
        ],
      },
      {
        heading: 'Free tools',
        items: [
          { icon: ScanSearch, label: 'Audit and grade', href: '/tools#tools-audit', description: 'PDP audit, AI visibility, stack detector' },
          { icon: BarChart3, label: 'Calculate', href: '/tools#tools-calculate', description: 'ROI, savings, A/B significance' },
          { icon: Wand2, label: 'Build and preview', href: '/tools#tools-build', description: 'Widget builder, link in bio, QR' },
          { icon: FileText, label: 'Generate', href: '/tools#tools-generate', description: 'Briefs, scripts, review links' },
          { icon: Wrench, label: 'All free tools', href: '/tools', description: `${SITE_COUNTS.freeTools} tools, no signup` },
        ],
      },
      {
        heading: 'Community',
        items: [
          { icon: Heart, label: 'Partners directory', href: '/partners', description: 'Agencies and integrations' },
          { icon: Megaphone, label: 'Become a partner', href: '/become-a-partner', description: 'Referral and agency programmes' },
          { icon: Activity, label: 'Status', href: '/status', description: 'Live uptime and incidents' },
          { icon: Newspaper, label: 'Press kit', href: '/press', description: 'Logos, facts, founder bio' },
        ],
      },
    ],
    featured: {
      eyebrow: 'Start here',
      title: 'From signup to first live widget',
      description:
        'The getting-started path: connect a source, approve content, tag products, publish, then read the first attributed order.',
      image: pickShot(7),
      ctaLabel: 'Open getting started',
      ctaHref: '/getting-started',
    },
  },
]

export function SiteHeader() {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [scrolled, setScrolled] = React.useState(false)
  const [openGroup, setOpenGroup] = React.useState<string | null>(null)
  const mobileNavRef = React.useRef<HTMLDivElement | null>(null)
  const mobileTriggerRef = React.useRef<HTMLButtonElement | null>(null)
  // Debounced close, prevents the menu from snapping shut when the
  // cursor briefly leaves the trigger button on its way to the panel,
  // and gives the user 200ms of grace period when moving across menus.
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelClose = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])
  const scheduleClose = React.useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenGroup(null), 220)
  }, [cancelClose])
  const openImmediately = React.useCallback(
    (label: string) => {
      cancelClose()
      setOpenGroup(label)
    },
    [cancelClose],
  )

  // Roving-focus keyboard model for the desktop bar. Following the WAI-ARIA
  // menubar pattern: only one top-level trigger is in the tab order at a time
  // (`focusIndex`); Left/Right/Home/End move between triggers, Down opens a
  // mega panel and drops focus into it, and Up/Escape from inside a panel
  // return focus to its trigger. Mouse/hover behaviour is untouched.
  const [focusIndex, setFocusIndex] = React.useState(0)
  const triggerRefs = React.useRef<(HTMLElement | null)[]>([])

  const focusFirstPanelItem = React.useCallback((label: string) => {
    // The panel mounts on the next paint after openGroup updates, so defer
    // the focus to the next frame.
    requestAnimationFrame(() => {
      const panel = document.getElementById(`mega-${label}`)
      panel?.querySelector<HTMLElement>('a[href], button')?.focus()
    })
  }, [])

  const returnToTrigger = React.useCallback((label: string) => {
    setOpenGroup(null)
    const i = nav.findIndex((g) => g.label === label)
    if (i >= 0) {
      setFocusIndex(i)
      triggerRefs.current[i]?.focus()
    }
  }, [])

  const onBarKeyDown = React.useCallback(
    (e: React.KeyboardEvent, i: number) => {
      const count = nav.length
      const moveTo = (idx: number) => {
        const next = (idx + count) % count
        setFocusIndex(next)
        triggerRefs.current[next]?.focus()
        // Keep an already-open menu in step as focus rides across the bar.
        if (openGroup) {
          const g = nav[next]
          if (g.kind === 'mega') openImmediately(g.label)
          else setOpenGroup(null)
        }
      }
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          moveTo(i + 1)
          break
        case 'ArrowLeft':
          e.preventDefault()
          moveTo(i - 1)
          break
        case 'Home':
          e.preventDefault()
          moveTo(0)
          break
        case 'End':
          e.preventDefault()
          moveTo(count - 1)
          break
        case 'ArrowDown': {
          const g = nav[i]
          if (g.kind === 'mega') {
            e.preventDefault()
            openImmediately(g.label)
            focusFirstPanelItem(g.label)
          }
          break
        }
      }
    },
    [openGroup, openImmediately, focusFirstPanelItem],
  )

  const onPanelKeyDown = React.useCallback(
    (e: React.KeyboardEvent, label: string) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        returnToTrigger(label)
        return
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const panel = e.currentTarget as HTMLElement
      const items = Array.from(panel.querySelectorAll<HTMLElement>('a[href], button'))
      if (items.length === 0) return
      const idx = items.indexOf(document.activeElement as HTMLElement)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        items[Math.min(idx + 1, items.length - 1)]?.focus()
      } else {
        e.preventDefault()
        if (idx <= 0) returnToTrigger(label)
        else items[idx - 1]?.focus()
      }
    },
    [returnToTrigger],
  )

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  React.useEffect(() => {
    if (!openGroup) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenGroup(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openGroup])

  // Lock background scroll while the mobile menu is open so page content
  // (and any autoplay video below it) can't peek through or scroll under
  // the panel.
  React.useEffect(() => {
    if (!mobileOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileOpen])

  // Accessible dialog behaviour for the mobile nav drawer: Escape closes
  // it, Tab is trapped inside the panel (mirrors cookie-settings-modal.tsx
  // and add-to-cart-modal.tsx), and focus moves in on open / restores to
  // the hamburger trigger on close. Found missing in the 29 Aug 2026 SEO/UX
  // audit — the panel previously had no role="dialog"/aria-modal and no
  // focus management at all, so keyboard/screen-reader users could tab
  // straight past it into hidden header content behind the overlay.
  React.useEffect(() => {
    if (!mobileOpen) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setMobileOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      const node = mobileNavRef.current
      if (!node) return
      const focusables = node.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    const t = window.setTimeout(() => {
      mobileNavRef.current
        ?.querySelector<HTMLElement>('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
        ?.focus()
    }, 0)

    return () => {
      document.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
      mobileTriggerRef.current?.focus()
    }
  }, [mobileOpen])

  return (
    <header
      className={cn(
        'sticky top-0 w-full transition',
        // When the mobile menu is open, lift the whole header (and its
        // in-flow dropdown) above page-level overlays — autoplay video
        // demos and the fixed add-to-cart modal (z-100/101) otherwise
        // bleed through the opaque panel because the header is its own
        // stacking context.
        mobileOpen ? 'z-[120]' : 'z-40',
        // A full-width sticky bar only ever wants a single bottom hairline.
        // The `.glass` utility adds a border on all four sides, which renders
        // stray top/side lines across the header on scroll, so apply the glass
        // blur background directly and keep just one clean bottom divider.
        scrolled || openGroup || mobileOpen
          ? 'border-b border-border/60 bg-background/75 backdrop-blur-xl backdrop-saturate-150'
          : 'bg-transparent',
      )}
    >
      <div className="container-wide flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center" aria-label="Idukki home">
          <BrandLogo />
          <span className="sr-only">Idukki</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {nav.map((group, i) => {
            if (group.kind === 'link') {
              return (
                <Link
                  key={group.label}
                  href={group.href}
                  ref={(el) => {
                    triggerRefs.current[i] = el
                  }}
                  tabIndex={focusIndex === i ? 0 : -1}
                  onKeyDown={(e) => onBarKeyDown(e, i)}
                  onFocus={() => setFocusIndex(i)}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground"
                >
                  {group.label}
                  {group.badge && (
                    <span className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                      {group.badge}
                    </span>
                  )}
                </Link>
              )
            }
            const isOpen = openGroup === group.label
            return (
              <div
                key={group.label}
                onMouseEnter={() => openImmediately(group.label)}
                onMouseLeave={scheduleClose}
                className="static"
              >
                <button
                  type="button"
                  ref={(el) => {
                    triggerRefs.current[i] = el
                  }}
                  tabIndex={focusIndex === i ? 0 : -1}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm transition',
                    isOpen
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                  )}
                  onClick={() => (isOpen ? setOpenGroup(null) : openImmediately(group.label))}
                  onFocus={() => setFocusIndex(i)}
                  onKeyDown={(e) => onBarKeyDown(e, i)}
                  aria-expanded={isOpen}
                  aria-haspopup="menu"
                  aria-controls={`mega-${group.label}`}
                >
                  {group.label}
                  <ChevronDown className={cn('h-3 w-3 transition', isOpen && 'rotate-180')} />
                </button>
                {isOpen && (
                  <div
                    id={`mega-${group.label}`}
                    role="region"
                    aria-label={`${group.label} menu`}
                    className="absolute inset-x-0 top-full z-40 animate-fade-up"
                    onMouseEnter={() => openImmediately(group.label)}
                    onMouseLeave={scheduleClose}
                    onKeyDown={(e) => onPanelKeyDown(e, group.label)}
                  >
                    {/* Invisible bridge so the cursor doesn't fall into a
                        gap between the trigger and the panel. */}
                    <div className="h-3 w-full" />
                    <div className="container-wide pb-6">
                      <MegaPanel group={group} onClose={() => setOpenGroup(null)} />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <Button variant="ghost" asChild>
            <a href="https://app.idukki.io">{t.header.signIn}</a>
          </Button>
          <Button asChild>
            <a
              href="https://app.apollo.io/#/meet/idukki/30-min-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.header.bookDemo}
            </a>
          </Button>
        </div>

        <div className="hidden items-center gap-1 max-lg:flex">
          <Button
            ref={mobileTriggerRef}
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div
          ref={mobileNavRef}
          id="mobile-nav-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t bg-background shadow-2xl lg:hidden"
        >
          <nav className="container-wide flex flex-col py-4" aria-label="Mobile">
            {nav.map((group) => (
              <div key={group.label} className="py-1">
                {group.kind === 'mega' ? (
                  <details className="group">
                    <summary className="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-base font-medium hover:bg-secondary">
                      {group.label}
                      <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
                    </summary>
                    <div className="ml-3 mt-1 space-y-3 border-l border-border pl-3">
                      {group.columns.map((col) => (
                        <div key={col.heading}>
                          <p className="px-3 pt-2 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                            {col.heading}
                          </p>
                          <ul className="flex flex-col">
                            {col.items.map((it) => {
                              const Icon = it.icon
                              return (
                                <li key={it.href}>
                                  <Link
                                    href={it.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-start gap-3 rounded-md px-3 py-2 hover:bg-secondary"
                                  >
                                    {Icon && (
                                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                    )}
                                    <span>
                                      <span className="block text-sm font-medium">{it.label}</span>
                                      {it.description && (
                                        <span className="block text-xs text-muted-foreground">
                                          {it.description}
                                        </span>
                                      )}
                                    </span>
                                  </Link>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                ) : (
                  <Link
                    href={group.href}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-md px-3 py-2 text-base hover:bg-secondary"
                  >
                    {group.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="mt-4 flex gap-2">
              <Button variant="outline" asChild className="flex-1">
                <a href="https://app.idukki.io">{t.header.signIn}</a>
              </Button>
              <Button asChild className="flex-1">
                <a
              href="https://app.apollo.io/#/meet/idukki/30-min-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.header.bookDemo}
            </a>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}

function MegaPanel({
  group,
  onClose,
}: {
  group: Extract<NavGroup, { kind: 'mega' }>
  onClose: () => void
}) {
  const customers = (brand.customerLogos || [])
    .filter((logo) => !/-short\.svg$/.test(logo.sourceUrl))
    .slice(0, 8)
  const archive = resolveArchivePosts(group.archiveQuery, 3)
  // Cap the panel to what's actually visible below the header so the
  // bottom of long menus (e.g. Platform → Ship trust) never gets
  // clipped when the page is scrolled mid-way. The container is the
  // popover itself, not the inner grid, so the bordered card stays
  // visually intact while the content area scrolls inside it.
  return (
    <div className="flex max-h-[calc(100vh-6rem)] flex-col overflow-y-auto overscroll-contain rounded-2xl border bg-popover shadow-2xl">
      {(group.tagline || group.stats) && (
        <div className="flex flex-col gap-3 border-b px-6 pb-4 pt-5 md:flex-row md:items-center md:justify-between">
          {group.tagline && (
            <p className="max-w-xl text-sm text-muted-foreground">{group.tagline}</p>
          )}
          {group.stats && group.stats.length > 0 && (
            <ul className="flex flex-wrap gap-x-5 gap-y-2">
              {group.stats.map((s) => (
                <li key={s.label} className="flex items-baseline gap-1.5">
                  <span className="font-display text-base font-medium">{s.value}</span>
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {s.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 p-6">
        <div className={cn('col-span-12', group.featured ? 'lg:col-span-8' : 'lg:col-span-12')}>
          <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
            {group.columns.map((col) => (
              <div key={col.heading}>
                <p className="mb-3 flex items-baseline justify-between text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <span>{col.heading}</span>
                  <span className="opacity-70">{col.items.length}</span>
                </p>
                <ul className="flex flex-col gap-1">
                  {col.items.map((it) => {
                    const Icon = it.icon
                    return (
                      <li key={it.href}>
                        <Link
                          href={it.href}
                          onClick={onClose}
                          target={it.external ? '_blank' : undefined}
                          rel={it.external ? 'noopener noreferrer' : undefined}
                          className="group/item flex items-start gap-3 rounded-lg px-2.5 py-2 transition hover:bg-secondary"
                        >
                          {Icon && (
                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground transition group-hover/item:border-primary/40 group-hover/item:bg-primary/5 group-hover/item:text-primary">
                              <Icon className="h-4 w-4" />
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                              {it.label}
                              {it.external && (
                                <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                              )}
                              {it.badge && (
                                <span
                                  className={cn(
                                    'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                                    it.badge === 'NEW' &&
                                      'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
                                    it.badge === 'POPULAR' &&
                                      'bg-amber-500/15 text-amber-700 dark:text-amber-400',
                                    !['NEW', 'POPULAR'].includes(it.badge) &&
                                      'bg-primary/10 text-primary',
                                  )}
                                >
                                  {it.badge}
                                </span>
                              )}
                            </span>
                            {it.description && (
                              <span className="mt-0.5 block text-xs text-muted-foreground">
                                {it.description}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {group.featured && (
          <aside className="col-span-12 lg:col-span-4">
            <Link
              href={group.featured.ctaHref}
              target={group.featured.external ? '_blank' : undefined}
              rel={group.featured.external ? 'noopener noreferrer' : undefined}
              onClick={onClose}
              className="group/featured flex h-full flex-col overflow-hidden rounded-xl border bg-card transition hover:border-primary/40 hover:shadow-md"
            >
              {group.featured.image && (
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-gradient-to-br from-muted to-primary/5">
                  <Image
                    src={group.featured.image}
                    alt=""
                    fill
                    sizes="(max-width: 1024px) 100vw, 360px"
                    // The featured images are customer logos/wordmarks, which
                    // object-cover crops into nonsense. Contain + pad so the
                    // mark always sits centred and whole on a soft panel.
                    className="object-contain p-6 transition group-hover/featured:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground backdrop-blur-sm">
                    {group.featured.eyebrow}
                  </span>
                </div>
              )}
              <div className="flex flex-1 flex-col p-4">
                <h3 className="font-display text-lg font-medium leading-snug tracking-tight">
                  {group.featured.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{group.featured.description}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  {group.featured.ctaLabel} <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          </aside>
        )}
      </div>

      {archive.length > 0 && (
        <div className="border-t bg-secondary/30 px-6 py-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              From the idukki.io archive
            </p>
          </div>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {archive.map((p) => {
              // Force a rotated screenshot here, never the per-page
              // cover, since some imported posts have author headshots
              // captured as their first image.
              const cover = rotatedCoverFor(p)?.ref
              return (
                <li key={p.slug}>
                  <Link
                    href={`/${p.slug}`}
                    onClick={onClose}
                    className="group/post flex h-full gap-3 rounded-lg border bg-background p-2.5 transition hover:border-primary/40 hover:shadow-sm"
                  >
                    {cover && (
                      <span className="relative h-14 w-20 shrink-0 overflow-hidden rounded-md bg-muted">
                        <Image
                          src={cover}
                          alt=""
                          fill
                          sizes="80px"
                          className="object-cover transition group-hover/post:scale-105"
                        />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Blog
                      </span>
                      <span className="mt-0.5 line-clamp-2 block text-xs font-medium leading-snug">
                        {p.h1 || p.title}
                      </span>
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {customers.length > 0 && (
        <div className="border-t bg-white px-6 py-4 dark:bg-secondary/40">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <p className="whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Trusted by
            </p>
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-3">
              {customers.map((logo) => (
                <li key={logo.ref} className="flex h-9 items-center justify-center">
                  <Image
                    src={logo.ref}
                    alt=""
                    width={120}
                    height={32}
                    className="h-7 w-auto object-contain opacity-90 transition hover:opacity-100 dark:brightness-0 dark:invert"
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

function BrandLogo() {
  const realLogo = brand.assets?.logo?.ref
  if (realLogo) {
    // Idukki wordmark from the original site already includes the
    // brand name. Render it large and let it carry the brand on its
    // own, no duplicated text label needed.
    return (
      <span className="flex h-10 items-center">
        <Image
          src={realLogo}
          alt="Idukki"
          width={160}
          height={40}
          priority
          className="h-9 w-auto object-contain md:h-10"
        />
      </span>
    )
  }
  return (
    <svg
      width="160"
      height="40"
      viewBox="0 0 160 40"
      fill="none"
      aria-label="Idukki"
      className="h-9 w-auto md:h-10"
    >
      {/* Brand mark: lime + navy bars (per brand cues). Uses the brand-lime /
          brand-navy tokens, not raw hex, so a future re-skin only touches
          app/globals.css. See DESIGN_SYSTEM.md §1. */}
      <rect x="2" y="4" width="32" height="32" rx="7" className="fill-brand-lime" />
      <path
        d="M12 14v14M18 17v11M24 11v17"
        className="stroke-brand-navy"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <text
        x="44"
        y="27"
        fontFamily="var(--font-jakarta, 'Plus Jakarta Sans', system-ui, sans-serif)"
        fontSize="22"
        fontWeight="700"
        fill="currentColor"
      >
        Idukki
      </text>
    </svg>
  )
}
